import { Injectable, Inject, Optional } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin, throwError, EMPTY } from 'rxjs';
import { catchError, delay, map, switchMap, expand, reduce } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BASE_PATH } from '@app/variables';
import { environment } from 'environments/environment';
import {
  Equipment,
  EquipmentMake,
  EquipmentModel,
  EquipmentPartPackage,
  EquipmentPurchaseHistory,
  RecentMakeModelSearch,
  EquipmentPart,
  EquipmentImage
} from '@app/model/equipment';

@Injectable({
  providedIn: 'root'
})
export class EquipmentService {
  private equipmentList: Equipment[] = [];
  private equipmentList$ = new BehaviorSubject<Equipment[]>([]);

  private readonly mockMakes: EquipmentMake[] = [
    {
      name: 'TOYOTA',
      models: [
        { model: '7FBR15', startDate: '2001-01-01' },
        { model: '8FD30', startDate: '2005-06-01' },
        { model: '8FBN25', startDate: '2010-03-01' }
      ],
      mastOptions: [
        { code: 'FSV', description: '3-stage, full free lift, 2 side + 1 central cyl.' },
        { code: 'FV', description: '2-stage, full free lift, 2 side + 1 central cyl.' },
        { code: 'SV', description: '2-stage, limit. freelift, 2 side cyl.' },
        { code: 'V', description: '2-stage, view mast, with 2 side cyl.' }
      ]
    },
    {
      name: 'NOBLELIFT',
      models: [
        { model: 'PS16N', startDate: '2015-01-01' },
        { model: 'FE4P20', startDate: '2018-01-01' }
      ],
      mastOptions: [
        { code: 'STD', description: 'Standard mast' },
        { code: 'FF', description: 'Full free lift mast' }
      ]
    },
    {
      name: 'TAILIFT',
      models: [
        { model: 'FD30', startDate: '2008-01-01' },
        { model: 'FD25', startDate: '2009-01-01' }
      ],
      mastOptions: [
        { code: 'STD', description: 'Standard 2-stage mast' },
        { code: 'TFM', description: 'Triple full free mast' }
      ]
    },
    {
      name: 'LINDE',
      models: [
        { model: 'R20-03', startDate: '2012-01-01' },
        { model: 'R20S-02 EX', startDate: '2014-01-01' },
        { model: 'H30D', startDate: '2010-01-01' }
      ],
      mastOptions: [
        { code: 'DZ', description: 'Duplex mast' },
        { code: 'TZ', description: 'Triplex mast' },
        { code: 'QZ', description: 'Quadruplex mast' }
      ]
    },
    {
      name: 'KOMATSU',
      models: [
        { model: 'FD25T-17', startDate: '2011-01-01' },
        { model: 'FB15-12', startDate: '2013-01-01' }
      ],
      mastOptions: [
        { code: 'STD', description: 'Standard mast' },
        { code: 'FFT', description: 'Full free triple mast' }
      ]
    },
    {
      name: 'HYSTER',
      models: [
        { model: 'H2.5FT', startDate: '2009-01-01' },
        { model: 'J2.0XN', startDate: '2016-01-01' }
      ],
      mastOptions: [
        { code: 'STD', description: 'Standard 2-stage mast' },
        { code: 'FFL', description: 'Full free lift mast' }
      ]
    }
  ];

  private readonly recentSearches: RecentMakeModelSearch[] = [
    { make: 'TOYOTA', model: '7FBR15', segments: ['SEPARATOR'], serialRange: '' },
    { make: 'NOBLELIFT', model: 'PS16N', segments: ['ELECTRIC', 'SEPARATOR'], serialRange: '' },
    { make: 'TAILIFT', model: 'FD30', segments: [], serialRange: 'AA00001 - AA99999' },
    { make: 'LINDE', model: 'R20-03', segments: ['115'], serialRange: 'U050001 - Z129999' },
    { make: 'LINDE', model: 'R20S-02 EX', segments: ['115', 'SEPARATOR'], serialRange: '' }
  ];

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) protected basePath: string
  ) {
    if (!this.basePath) {
      this.basePath = '/';
    }
    const saved = localStorage.getItem('my_equipment_list');
    if (saved) {
      try {
        this.equipmentList = JSON.parse(saved);
      } catch {
        this.equipmentList = [];
      }
    }
    this.equipmentList$.next(this.equipmentList);
  }

  getEquipmentList(
    page?: number,
    pageSize?: number
  ): Observable<{ equipment: Equipment[]; total: number }> {
    const params: any = {};
    if (page != null && pageSize != null) {
      params.Page = String(page);
      params.PageSize = String(pageSize);
    }
    return this.httpClient.get<any>(`${this.basePath}/api/myEquipment`, { params }).pipe(
      map((res) => {
        let list: any[] = [];
        let total = 0;
        if (res && Array.isArray(res)) {
          list = res;
          total = res.length;
        } else if (res && res.data && Array.isArray(res.data.collection)) {
          list = res.data.collection;
          total = res.data.total != null ? res.data.total : list.length;
        } else if (res && res.collection && Array.isArray(res.collection)) {
          list = res.collection;
          total = res.total != null ? res.total : list.length;
        }

        const mapped = list.map((item) => ({
          id: item.id,
          name: item.name || '',
          ownReference: item.custom_reference || item.customReference || '',
          location: item.location || '',
          productionMonth: item.production_month || item.productionMonth || null,
          productionYear:
            item.year_of_manufacture || item.yearOfManufacture || item.productionYear || null,
          make:
            item.brand_name_manual || item.brandNameManual || (item.brand ? item.brand.name : ''),
          model:
            item.model_name_manual ||
            item.modelNameManual ||
            (item.equipment_model ? item.equipment_model.name : ''),
          serialNumber: item.serial_number || item.serialNumber || '',
          segment: item.segment || '',
          mastType: item.mast_type || item.mastType || '',
          imageUrl: item.default_image || item.defaultImage || item.imageUrl || '',
          addedDate: item.created_at ? new Date(item.created_at) : new Date(),

          userId: item.user_id || item.userId,
          equipmentModelId: item.equipment_model_id || item.equipmentModelId,
          equipmentModelPrefixId: item.equipment_model_prefix_id || item.equipmentModelPrefixId,
          brandId: item.brand_id || item.brandId,
          brandNameManual: item.brand_name_manual || item.brandNameManual,
          modelNameManual: item.model_name_manual || item.modelNameManual,
          licensePlate: item.license_plate || item.licensePlate,
          hourMeter: item.hour_meter || item.hourMeter,
          status: item.status,
          notes: item.notes,
          // Thông tin khung gầm
          chassisSerialNumber: item.chassis_serial_number || item.chassisSerialNumber,
          // Thông tin động cơ
          engineBrand: item.engine_brand || item.engineBrand,
          engineModel: item.engine_model || item.engineModel,
          engineSerialNumber: item.engine_serial_number || item.engineSerialNumber,
          // Thông tin hộp số
          transmissionBrand: item.transmission_brand || item.transmissionBrand,
          transmissionModel: item.transmission_model || item.transmissionModel,
          transmissionSerialNumber:
            item.transmission_serial_number || item.transmissionSerialNumber,
          // Thông tin info cầu xe
          axleBrand: item.axle_brand || item.axleBrand,
          axleModel: item.axle_model || item.axleModel,
          axleSerialNumber: item.axle_serial_number || item.axleSerialNumber,
          // Thông tin info khung nâng
          mastModel: item.mast_model || item.mastModel,
          mastSerialNumber: item.mast_serial_number || item.mastSerialNumber,
          // Thông tin info chung
          origin: item.origin || item.origin,
          // Nhiên liệu & Tải trọng
          fuelType: item.fuel_type || item.fuelType,
          capacity: item.capacity || item.capacity,
          // Tire information
          steeringTireSize: item.steering_tire_size || item.steeringTireSize || '',
          steeringTireType: item.steering_tire_type || item.steeringTireType || '',
          loadTireSize: item.load_tire_size || item.loadTireSize || '',
          loadTireType: item.load_tire_type || item.loadTireType || '',
          spareTireSize: item.spare_tire_size || item.spareTireSize || '',
          spareTireType: item.spare_tire_type || item.spareTireType || '',
          extendImages: (item.extend_images || []).map((img: any) => ({
            id: img.id,
            equipment_id: img.equipment_id || '',
            image_url: img.image_url || '',
            is_main: img.is_main || false,
            display_order: img.display_order != null ? img.display_order : 0
          }))
        }));
        this.equipmentList = mapped;
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return { equipment: mapped, total };
      }),
      catchError((err) => {
        console.error('Error fetching equipment from API, falling back to LocalStorage', err);
        return of({ equipment: this.equipmentList, total: this.equipmentList.length });
      })
    );
  }

  addEquipment(equipment: Equipment): Observable<any> {
    const payload = {
      name: `${equipment.make} ${equipment.model}`,
      brand_id: equipment.brandId || null,
      equipment_model_id: equipment.equipmentModelId || null,
      equipment_model_prefix_id: equipment.equipmentModelPrefixId || null,
      serial_number: equipment.serialNumber,
      custom_reference: equipment.ownReference,
      year_of_manufacture: equipment.productionYear || null,
      location: equipment.location || '',
      brand_name_manual: equipment.make,
      model_name_manual: equipment.model,
      status: equipment.status || 'active',
      notes: equipment.notes || '',
      hour_meter: equipment.hourMeter || 0,
      license_plate: equipment.licensePlate || null,
      // Thông tin khung gầm
      chassis_serial_number: equipment.chassisSerialNumber || null,
      // Thông tin động cơ
      engine_brand: equipment.engineBrand || null,
      engine_model: equipment.engineModel || null,
      engine_serial_number: equipment.engineSerialNumber || null,
      // Thông tin hộp số
      transmission_brand: equipment.transmissionBrand || null,
      transmission_model: equipment.transmissionModel || null,
      transmission_serial_number: equipment.transmissionSerialNumber || null,
      // Thông tin cầu xe
      axle_brand: equipment.axleBrand || null,
      axle_model: equipment.axleModel || null,
      axle_serial_number: equipment.axleSerialNumber || null,
      // Thông tin khung nâng
      mast_type: equipment.mastType || null,
      mast_model: equipment.mastModel || null,
      mast_serial_number: equipment.mastSerialNumber || null,
      // Thông tin chung
      origin: equipment.origin || null,
      // Nhiên liệu & Tải trọng
      fuel_type: equipment.fuelType || null,
      capacity: equipment.capacity || null,
      // Tire information
      steering_tire_size: equipment.steeringTireSize || null,
      steering_tire_type: equipment.steeringTireType || null,
      load_tire_size: equipment.loadTireSize || null,
      load_tire_type: equipment.loadTireType || null,
      spare_tire_size: equipment.spareTireSize || null,
      spare_tire_type: equipment.spareTireType || null
    };

    return this.httpClient.post<any>(`${this.basePath}/api/myEquipment`, payload).pipe(
      map((res) => {
        const responseData = res && res.data ? res.data : res;
        const newEq: Equipment = {
          ...equipment,
          id: responseData.id || res.id || this.generateId(),
          addedDate: responseData.created_at
            ? new Date(responseData.created_at)
            : res.created_at
              ? new Date(res.created_at)
              : new Date()
        };
        // Check if item already exists to prevent duplicate cached entries
        const idx = this.equipmentList.findIndex((e) => e.id === newEq.id);
        if (idx > -1) {
          this.equipmentList[idx] = newEq;
        } else {
          this.equipmentList.push(newEq);
        }
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return newEq;
      }),
      catchError((err) => {
        console.error('API addEquipment failed, falling back to LocalStorage', err);
        const newEq: Equipment = {
          ...equipment,
          id: this.generateId(),
          addedDate: new Date()
        };
        this.equipmentList.push(newEq);
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return of(newEq);
      })
    );
  }

  updateEquipment(id: string, equipment: Equipment): Observable<any> {
    const payload = {
      id: id,
      name: `${equipment.make} ${equipment.model}`,
      brand_id: equipment.brandId || null,
      equipment_model_id: equipment.equipmentModelId || null,
      equipment_model_prefix_id: equipment.equipmentModelPrefixId || null,
      serial_number: equipment.serialNumber,
      custom_reference: equipment.ownReference,
      year_of_manufacture: equipment.productionYear || null,
      location: equipment.location || '',
      brand_name_manual: equipment.make,
      model_name_manual: equipment.model,
      status: equipment.status || 'active',
      notes: equipment.notes || '',
      hour_meter: equipment.hourMeter || 0,
      license_plate: equipment.licensePlate || null,
      // Thông tin khung gầm
      chassis_serial_number: equipment.chassisSerialNumber || null,
      // Thông tin động cơ
      engine_brand: equipment.engineBrand || null,
      engine_model: equipment.engineModel || null,
      engine_serial_number: equipment.engineSerialNumber || null,
      // Thông tin hộp số
      transmission_brand: equipment.transmissionBrand || null,
      transmission_model: equipment.transmissionModel || null,
      transmission_serial_number: equipment.transmissionSerialNumber || null,
      // Thông tin info cầu xe
      axle_brand: equipment.axleBrand || null,
      axle_model: equipment.axleModel || null,
      axle_serial_number: equipment.axleSerialNumber || null,
      // Thông info khung nâng
      mast_type: equipment.mastType || null,
      mast_model: equipment.mastModel || null,
      mast_serial_number: equipment.mastSerialNumber || null,
      // Thông tin info chung
      origin: equipment.origin || null,
      // Nhiên liệu & Tải trọng
      fuel_type: equipment.fuelType || null,
      capacity: equipment.capacity || null,
      // Tire information
      steering_tire_size: equipment.steeringTireSize || null,
      steering_tire_type: equipment.steeringTireType || null,
      load_tire_size: equipment.loadTireSize || null,
      load_tire_type: equipment.loadTireType || null,
      spare_tire_size: equipment.spareTireSize || null,
      spare_tire_type: equipment.spareTireType || null
    };

    return this.httpClient.put<any>(`${this.basePath}/api/myEquipment/${id}`, payload).pipe(
      map((res) => {
        const responseData = res && res.data ? res.data : res;
        const updatedEq: Equipment = {
          ...equipment,
          id: id,
          imageUrl:
            responseData.default_image ||
            responseData.defaultImage ||
            responseData.imageUrl ||
            res.default_image ||
            res.defaultImage ||
            res.imageUrl ||
            equipment.imageUrl ||
            ''
        };
        const idx = this.equipmentList.findIndex((e) => e.id === id);
        if (idx > -1) {
          this.equipmentList[idx] = updatedEq;
        }
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return updatedEq;
      }),
      catchError((err) => {
        console.error('API updateEquipment failed, falling back to LocalStorage', err);
        const idx = this.equipmentList.findIndex((e) => e.id === id);
        if (idx > -1) {
          this.equipmentList[idx] = { ...equipment, id: id };
        }
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return of({ ...equipment, id: id });
      })
    );
  }

  uploadEquipmentImage(equipmentId: string, imageFile: File): Observable<any> {
    return this.httpClient
      .post<any>(`${this.basePath}/api/myEquipmentImages`, {
        equipment_id: equipmentId,
        image_url: null,
        is_main: false,
        display_order: 0
      })
      .pipe(
        switchMap((createRes) => {
          const imageId = createRes.data && createRes.data.id ? createRes.data.id : createRes.id;
          const formData = new FormData();
          formData.append('file', imageFile);
          return this.httpClient.put<any>(
            `${this.basePath}/api/myEquipmentImages/${imageId}/image`,
            formData
          );
        })
      );
  }

  setDefaultImage(equipmentId: string, imageFile: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', imageFile);
    return this.httpClient.put<any>(
      `${this.basePath}/api/myEquipment/${equipmentId}/image`,
      formData
    );
  }

  uploadEquipmentImages(equipmentId: string, files: File[]): Observable<any[]> {
    if (!files || files.length === 0) {
      return of([]);
    }
    const uploadAll = files.map((file) => this.uploadEquipmentImage(equipmentId, file));
    return forkJoin(uploadAll);
  }

  deleteEquipmentImage(imageId: string): Observable<any> {
    return this.httpClient.delete<any>(`${this.basePath}/api/myEquipmentImages/${imageId}`);
  }

  updateEquipmentImage(
    imageId: string,
    equipmentId: string,
    isMain: boolean,
    displayOrder: number
  ): Observable<any> {
    return this.httpClient.put<any>(`${this.basePath}/api/myEquipmentImages/${imageId}`, {
      equipment_id: equipmentId,
      is_main: isMain,
      display_order: displayOrder
    });
  }

  getCompatibleProducts(equipmentId: string): Observable<EquipmentPart[]> {
    return this.httpClient
      .get<any>(`${this.basePath}/api/myEquipment/${equipmentId}/compatible-products`)
      .pipe(
        map((res) => {
          let list: any[] = [];
          if (res && Array.isArray(res)) {
            list = res;
          } else if (res && res.data && Array.isArray(res.data.collection)) {
            list = res.data.collection;
          } else if (res && res.collection && Array.isArray(res.collection)) {
            list = res.collection;
          }

          return list.map((item): EquipmentPart => {
            const rawImg =
              item.image ||
              item.imageUrl ||
              (item.extend_product ? item.extend_product.default_image : '') ||
              '';
            let formattedImg = '/assets/media/products/placeholder.png';
            if (rawImg) {
              if (
                rawImg.startsWith('http://') ||
                rawImg.startsWith('https://') ||
                rawImg.startsWith('assets/')
              ) {
                formattedImg = rawImg;
              } else {
                const host = environment.backendhost || '';
                const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
                const cleanPath = rawImg.startsWith('/') ? rawImg : '/' + rawImg;
                formattedImg = cleanHost + cleanPath;
              }
            }

            const pId =
              item.product_id ||
              (item.extend_product ? item.extend_product.id : '') ||
              item.id ||
              '';

            return {
              id: pId,
              name:
                item.name ||
                item.product_name ||
                (item.extend_product ? item.extend_product.name : '') ||
                '',
              code:
                item.code ||
                item.sku ||
                (item.extend_product ? item.extend_product.code : '') ||
                '',
              brand:
                item.brand_name ||
                (item.brand ? item.brand.name : '') ||
                (item.extend_product ? item.extend_product.brand_name : '') ||
                '',
              price: item.price || (item.extend_product ? item.extend_product.price : 0) || 0,
              currency:
                item.currency ||
                (item.extend_product ? item.extend_product.currency : 'VND') ||
                'VND',
              imageUrl: formattedImg,
              quantity: 1
            };
          });
        }),
        catchError((err) => {
          console.error('Error fetching compatible products, falling back to empty list', err);
          return of([]);
        })
      );
  }

  getCustomProducts(equipmentId: string): Observable<EquipmentPart[]> {
    return this.httpClient
      .get<any>(`${this.basePath}/api/myEquipment/${equipmentId}/custom-products`)
      .pipe(
        map((res) => {
          let list: any[] = [];
          if (res && Array.isArray(res)) {
            list = res;
          } else if (res && res.data && Array.isArray(res.data.collection)) {
            list = res.data.collection;
          } else if (res && res.collection && Array.isArray(res.collection)) {
            list = res.collection;
          }

          return list.map((item): EquipmentPart => {
            const rawImg =
              item.image ||
              item.imageUrl ||
              (item.extend_product ? item.extend_product.default_image : '') ||
              '';
            let formattedImg = '/assets/media/products/placeholder.png';
            if (rawImg) {
              if (
                rawImg.startsWith('http://') ||
                rawImg.startsWith('https://') ||
                rawImg.startsWith('assets/')
              ) {
                formattedImg = rawImg;
              } else {
                const host = environment.backendhost || '';
                const cleanHost = host.endsWith('/') ? host.slice(0, -1) : host;
                const cleanPath = rawImg.startsWith('/') ? rawImg : '/' + rawImg;
                formattedImg = cleanHost + cleanPath;
              }
            }

            const pId =
              item.product_id ||
              (item.extend_product ? item.extend_product.id : '') ||
              item.id ||
              '';

            return {
              id: pId,
              name:
                item.name ||
                item.product_name ||
                (item.extend_product ? item.extend_product.name : '') ||
                '',
              code:
                item.code ||
                item.sku ||
                (item.extend_product ? item.extend_product.code : '') ||
                '',
              brand:
                item.brand_name ||
                (item.brand ? item.brand.name : '') ||
                (item.extend_product ? item.extend_product.brand_name : '') ||
                '',
              price: item.price || (item.extend_product ? item.extend_product.price : 0) || 0,
              currency:
                item.currency ||
                (item.extend_product ? item.extend_product.currency : 'VND') ||
                'VND',
              imageUrl: formattedImg,
              quantity: 1,
              sourceType: 'custom_product',
              sourceRefId: item.id || ''
            };
          });
        }),
        catchError((err) => {
          console.error('Error fetching custom products, falling back to empty list', err);
          return of([]);
        })
      );
  }

  removeEquipment(id: string): Observable<any> {
    return this.httpClient.delete<any>(`${this.basePath}/api/myEquipment/${id}`).pipe(
      map((res) => {
        this.equipmentList = this.equipmentList.filter((e) => e.id !== id);
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return res;
      }),
      catchError((err) => {
        console.error('API removeEquipment failed, falling back to LocalStorage', err);
        this.equipmentList = this.equipmentList.filter((e) => e.id !== id);
        this.saveToStorage();
        this.equipmentList$.next(this.equipmentList.slice());
        return of({ success: false, error: err });
      })
    );
  }

  getEquipmentById(id: string): Equipment | undefined {
    return this.equipmentList.find((e) => e.id === id);
  }

  fetchEquipmentById(id: string): Observable<Equipment> {
    return this.httpClient.get<any>(`${this.basePath}/api/myEquipment/${id}`).pipe(
      map((res) => {
        const item = res && res.data ? res.data : res;
        const mapped = {
          id: item.id || '',
          ownReference: item.custom_reference || '',
          location: item.location || '',
          productionYear: item.year_of_manufacture || null,
          productionMonth: null,
          make: item.brand_name_manual || (item.brand && item.brand.name) || '',
          model:
            item.model_name_manual || (item.equipment_model && item.equipment_model.name) || '',
          serialNumber: item.serial_number || '',
          segment: '',
          mastType: item.mast_type || '',
          imageUrl: item.default_image || '',
          addedDate: new Date(),

          userId: item.user_id,
          equipmentModelId:
            item.equipment_model_id || (item.equipment_model && item.equipment_model.id) || '',
          brandId: item.brand_id || (item.brand && item.brand.id) || '',
          brandNameManual: item.brand_name_manual || '',
          modelNameManual: item.model_name_manual || '',
          licensePlate: item.license_plate || '',
          hourMeter: item.hour_meter || 0,
          status: item.status || 'active',
          notes: item.notes || '',

          chassisSerialNumber: item.chassis_serial_number || '',
          engineBrand: item.engine_brand || '',
          engineModel: item.engine_model || '',
          engineSerialNumber: item.engine_serial_number || '',
          transmissionBrand: item.transmission_brand || '',
          transmissionModel: item.transmission_model || '',
          transmissionSerialNumber: item.transmission_serial_number || '',
          axleBrand: item.axle_brand || '',
          axleModel: item.axle_model || '',
          axleSerialNumber: item.axle_serial_number || '',
          mastModel: item.mast_model || '',
          mastSerialNumber: item.mast_serial_number || '',
          origin: item.origin || '',
          fuelType: item.fuel_type || '',
          capacity: item.capacity || '',
          // Tire information
          steeringTireSize: item.steering_tire_size || item.steeringTireSize || '',
          steeringTireType: item.steering_tire_type || item.steeringTireType || '',
          loadTireSize: item.load_tire_size || item.loadTireSize || '',
          loadTireType: item.load_tire_type || item.loadTireType || '',
          spareTireSize: item.spare_tire_size || item.spareTireSize || '',
          spareTireType: item.spare_tire_type || item.spareTireType || '',
          extendImages: (item.extend_images || []).map((img: any) => ({
            id: img.id,
            equipment_id: img.equipment_id || '',
            image_url: img.image_url || '',
            is_main: img.is_main || false,
            display_order: img.display_order != null ? img.display_order : 0
          }))
        } as unknown as Equipment;
        return mapped;
      }),
      catchError((err): Observable<Equipment> => {
        console.error('Error fetching equipment by id', err);
        const cached = this.getEquipmentById(id);
        return cached ? of(cached) : (throwError(() => err) as any);
      })
    );
  }

  getMakes(): Observable<EquipmentMake[]> {
    return this.httpClient.get<any>(`${this.basePath}/api/equipmentModels`).pipe(
      map((res) => {
        let list: any[] = [];
        if (res && Array.isArray(res)) {
          list = res;
        } else if (res && res.data && Array.isArray(res.data.collection)) {
          list = res.data.collection;
        } else if (res && res.collection && Array.isArray(res.collection)) {
          list = res.collection;
        }

        const makesMap = new Map<string, EquipmentMake>();

        list.forEach((item) => {
          const brandName = (item.brand_name || 'Khác').toUpperCase();
          const brandId = item.brand_id;

          if (!makesMap.has(brandName)) {
            const mockMatch = this.mockMakes.find((m) => m.name.toUpperCase() === brandName);
            const mastOptions = mockMatch
              ? mockMatch.mastOptions
              : [
                  { code: 'STD', description: 'Standard mast' },
                  { code: 'FFL', description: 'Full free lift mast' }
                ];

            makesMap.set(brandName, {
              id: brandId,
              name: brandName,
              models: [],
              mastOptions: mastOptions
            });
          }

          const makeObj = makesMap.get(brandName)!;
          const modelName = item.name || item.name_en || '';

          if (!makeObj.models.some((m) => m.model === modelName)) {
            const startDate = item.created_at
              ? new Date(item.created_at).toLocaleDateString('vi-VN')
              : '';
            makeObj.models.push({
              id: item.id,
              brandId: brandId,
              model: modelName,
              startDate: startDate
            });
          }
        });

        const result = Array.from(makesMap.values());
        if (result.length === 0) {
          return this.mockMakes;
        }
        return result;
      }),
      catchError((err) => {
        console.error('Error fetching equipment models, falling back to mock makes', err);
        return of(this.mockMakes);
      })
    );
  }

  getActiveBrandIds(): Observable<Set<string>> {
    const pageSize = 100;

    const fetchPage = (page: number) =>
      this.httpClient
        .get<any>(`${this.basePath}/api/equipmentModels`, {
          params: { Page: String(page), PageSize: String(pageSize) }
        })
        .pipe(
          map((res) => {
            let list: any[] = [];
            if (res && Array.isArray(res)) {
              list = res;
            } else if (res && res.data && Array.isArray(res.data.collection)) {
              list = res.data.collection;
            } else if (res && res.collection && Array.isArray(res.collection)) {
              list = res.collection;
            }
            const total = (res && res.data && res.data.total) || (res && res.total) || list.length;
            return {
              items: list,
              page,
              hasMore: list.length === pageSize && page * pageSize < total
            };
          })
        );

    return fetchPage(1).pipe(
      expand((res) => (res.hasMore ? fetchPage(res.page + 1) : EMPTY)),
      reduce((acc, res) => {
        res.items.forEach((item) => {
          const id = item.brand_id || (item.brand && item.brand.id);
          if (id) acc.add(id);
        });
        return acc;
      }, new Set<string>()),
      catchError((err) => {
        console.error('Error fetching active brand ids', err);
        return of(new Set<string>());
      })
    );
  }

  getBrands(
    page = 1,
    pageSize = 12,
    nameFilter = ''
  ): Observable<{ brands: EquipmentMake[]; total: number; hasMore: boolean }> {
    const params: any = {
      Page: String(page),
      PageSize: String(pageSize)
    };
    if (nameFilter) {
      params.Filters = 'name==' + nameFilter;
    }
    return this.httpClient.get<any>(`${this.basePath}/api/brands`, { params }).pipe(
      map((res) => {
        const data = res && res.data ? res.data : {};
        const collection = data.collection || [];
        const total = data.total || 0;
        const pagesize = data.pagesize || pageSize;
        const pageIndex = data.pageIndex || page;

        const brands = collection.map((item: any) => {
          const brandName = (item.name || 'Khác').toUpperCase();
          const mockMatch = this.mockMakes.find((m) => m.name.toUpperCase() === brandName);
          const mastOptions = mockMatch
            ? mockMatch.mastOptions
            : [
                { code: 'STD', description: 'Standard mast' },
                { code: 'FFL', description: 'Full free lift mast' }
              ];

          return {
            id: item.id,
            name: brandName,
            models: [],
            mastOptions: mastOptions
          } as EquipmentMake;
        });

        const hasMore = pageIndex * pagesize < total;
        return { brands, total, hasMore };
      }),
      catchError((err) => {
        console.error('Error fetching brands, falling back to mock makes', err);
        return of({ brands: this.mockMakes, total: this.mockMakes.length, hasMore: false });
      })
    );
  }

  /**
   * Fetch equipment models filtered by brand_id (client-side) with pagination.
   */
  //   getModelsByBrand(brandId: string, page = 1, pageSize = 50): Observable<{ models: EquipmentModel[]; total: number }> {
  //     return this.httpClient.get<any>(`${this.basePath}/api/equipmentModels`, {
  //       params: {
  //         Page: String(page),
  //         PageSize: String(pageSize)
  //       }
  //     }).pipe(
  //       map((res) => {
  //         let list: any[] = [];
  //         if (res && Array.isArray(res)) {
  //           list = res;
  //         } else if (res && res.data && Array.isArray(res.data.collection)) {
  //           list = res.data.collection;
  //         } else if (res && res.collection && Array.isArray(res.collection)) {
  //           list = res.collection;
  //         }
  //         const total = (res && res.data && res.data.total) || (res && res.total) || 0;

  //         const filtered = list.filter((item) => item.brand_id === brandId);

  //         const models = filtered.map((item) => ({
  //           id: item.id,
  //           brandId: item.brand_id || brandId,
  //           model: item.model_code || item.name_en || '',
  //           startDate: item.created_at
  //             ? new Date(item.created_at).toLocaleDateString('vi-VN')
  //             : ''
  //         }));
  //         return { models, total };
  //       }),
  //       catchError((err) => {
  //         console.error('Error fetching equipment models by brand', err);
  //         return of({ models: [] as EquipmentModel[], total: 0 });
  //       })
  //     );
  //   }

  getModelsByBrand(
    brandId: string,
    page = 1,
    pageSize = 10,
    keyword = ''
  ): Observable<{ models: EquipmentModel[]; total: number }> {
    const params: any = {
      Page: String(page),
      PageSize: String(pageSize),
      Sorts: '-created_at',
      brand_id: brandId
    };

    const searchKeyword = keyword.trim();

    if (searchKeyword) {
      params.Filters = '(model_code|description)@=*' + searchKeyword;
    }

    return this.httpClient
      .get<any>(`${this.basePath}/api/equipmentModels`, {
        params: params
      })
      .pipe(
        map((response) => {
          const data = response && response.data ? response.data : response || {};

          const collection = Array.isArray(data.collection)
            ? data.collection
            : Array.isArray(response)
              ? response
              : [];

          const models = collection.map((item: any) => ({
            id: item.id,
            brandId: item.brand_id || brandId,
            model: item.model_code || item.name_en || '',
            startDate: item.created_at ? new Date(item.created_at).toLocaleDateString('vi-VN') : ''
          }));

          return {
            models: models,
            total: Number(data.total || models.length)
          };
        }),
        catchError((error) => {
          console.error('Error fetching equipment models by brand', error);

          return of({
            models: [] as EquipmentModel[],
            total: 0
          });
        })
      );
  }

  getModelPrefixes(modelId: string): Observable<any[]> {
    if (!modelId) {
      return of([]);
    }

    return this.httpClient.get<any>(`${this.basePath}/api/equipmentModels/${modelId}`).pipe(
      map((res) => {
        if (res && res.success && res.data && res.data.prefixes) {
          return res.data.prefixes;
        }
        if (res && res.data && Array.isArray(res.data.prefixes)) {
          return res.data.prefixes;
        }
        return [];
      }),
      catchError((err) => {
        console.error('Error fetching equipment model prefixes', err);
        return of([]);
      })
    );
  }

  getRecentSearches(): Observable<RecentMakeModelSearch[]> {
    return of(this.recentSearches).pipe(delay(200));
  }

  getPurchaseHistory(equipmentId: string): Observable<EquipmentPurchaseHistory[]> {
    return this.httpClient.get<any>(`${this.basePath}/api/myEquipment/${equipmentId}/orders`).pipe(
      map((res) => {
        let list: any[] = [];
        if (res && Array.isArray(res)) {
          list = res;
        } else if (res && res.data && Array.isArray(res.data.collection)) {
          list = res.data.collection;
        } else if (res && res.collection && Array.isArray(res.collection)) {
          list = res.collection;
        }

        // Group the flat order items by order_id
        const ordersMap = new Map<string, EquipmentPurchaseHistory>();

        list.forEach((item) => {
          const orderId = item.order_id || item.id || '';
          if (!orderId) return;

          if (!ordersMap.has(orderId)) {
            const dateStr = item.order_created_at || item.created_at || item.date;
            ordersMap.set(orderId, {
              id: orderId,
              date: dateStr ? new Date(dateStr) : new Date(),
              orderCode: item.order_code || item.orderCode || item.code || '',
              totalPrice:
                item.order_total_price || item.total_price || item.totalPrice || item.total || 0,
              currency: item.currency || 'VND',
              status: item.status || 'Đã giao',
              parts: []
            });
          }

          const order = ordersMap.get(orderId)!;

          order.parts.push({
            id: item.product_id || item.id || '',
            name: item.product_name || item.name || '',
            code: item.product_code || item.code || item.sku || '',
            brand: item.brand_name || (item.brand ? item.brand.name : '') || '',
            price: item.price || 0,
            currency: item.currency || 'VND',
            imageUrl: item.image || item.imageUrl || '/assets/media/products/placeholder.png',
            quantity: item.quantity || 1,
            sourceType:
              item.source_type === 'custom_product' || item.source_type === 'part_package_item'
                ? item.source_type
                : undefined,
            sourceRefId: item.source_ref_id || null,
            // subTypeId for the cart API when the product has a classification
            productTypeId:
              (item.product_type_value && item.product_type_value.id) ||
              item.product_type_id ||
              null,
            productTypeText: item.product_type_text || null,
            partPackageId: item.part_package ? item.part_package.id : null,
            partPackageName: item.part_package ? item.part_package.name : null,
            partPackageDescription: item.part_package ? item.part_package.description : null,
            partPackageImage: item.part_package ? item.part_package.default_image : null
          });
        });

        return Array.from(ordersMap.values());
      }),
      catchError((err) => {
        console.error('Error fetching purchase history, falling back to empty list', err);
        return of([]);
      })
    );
  }

  getPartPackages(equipmentId: string): Observable<EquipmentPartPackage[]> {
    const equipment = this.getEquipmentById(equipmentId);
    const modelId = equipment ? equipment.equipmentModelId : null;
    const prefixId = equipment ? equipment.equipmentModelPrefixId : null;

    // Lọc theo model nếu có, ngược lại lấy tất cả gói phụ tùng.
    const listParams: any = {
      Page: '1',
      PageSize: '10'
    };
    if (modelId) {
      listParams.Filters = 'equipment_model_id==' + modelId;
    }

    return this.httpClient
      .get<any>(`${this.basePath}/api/partPackages`, {
        params: listParams
      })
      .pipe(
        switchMap((res) => {
          let packages: any[] = [];
          if (res && res.success && res.data && Array.isArray(res.data.collection)) {
            packages = res.data.collection;
          } else if (res && Array.isArray(res)) {
            packages = res;
          }

          // Filter by equipment prefix (if equipment has prefixId):
          // - Package WITHOUT prefix (shared package for the whole model) → still shown
          // - Package WITH prefix → only shown if it contains the equipment's prefixId
          // - If equipment has no prefixId → show all packages of the model
          if (prefixId) {
            packages = packages.filter((pkg) => {
              const pkgPrefixes: any[] = pkg.prefixes || [];
              if (pkgPrefixes.length === 0) {
                return true; // shared package, no prefix attached
              }
              return pkgPrefixes.some((p) => p.id === prefixId);
            });
          }

          if (packages.length === 0) {
            return of([]);
          }

          const packageObservables = packages.map((pkg) => {
            // The LIST endpoint /api/partPackages already returns default_image,
            // name, and description correctly — no need to call the DETAIL endpoint.
            // Only fetch the package items (parts list) separately.
            const items$ = this.httpClient.get<any>(
              `${this.basePath}/api/partPackages/${pkg.id}/items`,
              {
                params: {
                  Filters: '',
                  Sorts: '',
                  Page: '1',
                  PageSize: '100'
                }
              }
            );

            const extractItems = (itemRes: any): any[] => {
              if (
                itemRes &&
                itemRes.success &&
                itemRes.data &&
                Array.isArray(itemRes.data.collection)
              ) {
                return itemRes.data.collection;
              } else if (itemRes && itemRes.data && Array.isArray(itemRes.data.collection)) {
                return itemRes.data.collection;
              } else if (itemRes && Array.isArray(itemRes)) {
                return itemRes;
              }
              return [];
            };

            const buildPackageResult = (items: any[]) => {
                  const parts: EquipmentPart[] = items.map((entry) => {
                    // API /api/partPackages/{id}/items returns each entry with 2 keys:
                    //   - equipment_part_package_item: item info (quantity, product_type_id, product_type_text, ...)
                    //   - product: product info (name, price, product_type, product_type_value, ...)
                    const item = entry.equipment_part_package_item || entry;
                    const prod = entry.product || item.product || item.extend_product || {};

                    // Handle product classification:
                    // - If the product HAS a classification: product.product_type_value holds the real price/origin_price,
                    //   product.product_type holds the classification name, item.product_type_text holds the description text.
                    // - If the product has NO classification: both are null, use prod.price.
                    const typeValue = prod.product_type_value || null;
                    const typeInfo = prod.product_type || null;
                    const hasClassification = !!(typeValue && typeInfo);

                    const baseName = prod.name || item.product_name || '';
                    const typeName = typeInfo ? typeInfo.name : '';
                    const displayName =
                      hasClassification && typeName
                        ? `${baseName} - ${typeName}`
                        : baseName;

                    const price = hasClassification
                      ? typeValue.price != null
                        ? typeValue.price
                        : prod.price || 0
                      : prod.price || 0;

                    // subTypeId to pass to the cart API when the product has a classification.
                    // Per spec: take it from product.product_type_value.id (this equals
                    // equipment_part_package_item.product_type_id when classified).
                    const subTypeId =
                      (typeValue && typeValue.id) || item.product_type_id || null;

                    return {
                      id: item.product_id || prod.id || '',
                      name: displayName,
                      code: prod.product_code || prod.code || prod.sku || item.product_code || '',
                      brand: prod.brand_name || (prod.brand ? prod.brand.name : '') || '',
                      price: price,
                      currency: prod.currency || 'VND',
                      imageUrl: prod.default_image || '/assets/media/products/placeholder.png',
                      quantity: item.quantity || 1,
                      sourceType: 'part_package_item',
                      sourceRefId: item.id || '',
                      productTypeId: subTypeId,
                      productTypeText: item.product_type_text || null,
                      productTypeName: typeName || null,
                      isRequired: !!item.is_required,
                      selected: true
                    };
                  });

                  const totalPrice = parts.reduce(
                    (sum, part) => sum + part.price * part.quantity,
                    0
                  );

                  return {
                    id: pkg.id,
                    name: pkg.name,
                    description: pkg.description || '',
                    imageUrl: pkg.default_image || '',
                    parts: parts,
                    totalPrice: totalPrice,
                    currency: parts.length > 0 ? parts[0].currency : 'VND'
                  };
            };

            const emptyPackageResult = {
              id: pkg.id,
              name: pkg.name,
              description: pkg.description || '',
              imageUrl: pkg.default_image || '',
              parts: [],
              totalPrice: 0,
              currency: 'VND'
            };

            return items$.pipe(
              switchMap((itemRes) => {
                const items = extractItems(itemRes);
                // Total items in the package may exceed the PageSize=100 used above.
                // If so, re-fetch the full unpaginated list so totalPrice is computed
                // over the whole package, not just the first page.
                const apiTotal = itemRes && itemRes.data ? itemRes.data.total || items.length : items.length;
                if (apiTotal > items.length) {
                  return this.httpClient
                    .get<any>(`${this.basePath}/api/partPackages/${pkg.id}/items`, {
                      params: {
                        Filters: '',
                        Sorts: '',
                        Page: '1',
                        PageSize: String(apiTotal)
                      }
                    })
                    .pipe(
                      map((fullRes) => buildPackageResult(extractItems(fullRes))),
                      catchError(() => of(buildPackageResult(items)))
                    );
                }
                return of(buildPackageResult(items));
              }),
              catchError(() => of(emptyPackageResult))
              );
          });

          return forkJoin(packageObservables);
        }),
        catchError((err) => {
          console.error('Error fetching part packages', err);
          return of([]);
        })
      );
  }

  private generateId(): string {
    return 'eq-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);
  }

  /**
   * Lấy thống kê thiết bị của user hiện tại
   * GET /api/admin/my-equipment/statistics?userId=...&topLimit=...
   */
  getEquipmentStatistics(params?: {
    userId?: string;
    brandId?: string;
    equipmentModelId?: string;
    status?: string;
    fromDate?: string;
    toDate?: string;
    topLimit?: number;
  }): Observable<any> {
    const query: any = {};
    if (params) {
      if (params.userId) query.userId = params.userId;
      if (params.brandId) query.brandId = params.brandId;
      if (params.equipmentModelId) query.equipmentModelId = params.equipmentModelId;
      if (params.status) query.status = params.status;
      if (params.fromDate) query.fromDate = params.fromDate;
      if (params.toDate) query.toDate = params.toDate;
      if (params.topLimit != null) query.topLimit = String(params.topLimit);
    }
    return this.httpClient
      .get<any>(`${this.basePath}/api/admin/my-equipment/statistics`, { params: query })
      .pipe(
        catchError((err) => {
          console.error('Error fetching equipment statistics', err);
          return of({ success: false, message: err && err.message, data: null });
        })
      );
  }

  /**
   * Repair cost report — ranks the current user's equipment by total repair cost.
   * GET /api/myEquipment/reports/repairCost
   */
  getEquipmentRepairCostReport(params?: {
    fromDate?: string;
    toDate?: string;
    brandId?: string;
    equipmentModelId?: string;
    topLimit?: number;
  }): Observable<any> {
    const query: any = {};
    if (params) {
      if (params.fromDate) query.fromDate = params.fromDate;
      if (params.toDate) query.toDate = params.toDate;
      if (params.brandId) query.brandId = params.brandId;
      if (params.equipmentModelId) query.equipmentModelId = params.equipmentModelId;
      if (params.topLimit != null) query.topLimit = String(params.topLimit);
    }
    return this.httpClient
      .get<any>(`${this.basePath}/api/myEquipment/reports/repairCost`, { params: query })
      .pipe(
        catchError((err) => {
          console.error('Error fetching equipment repair cost report', err);
          return of({ success: false, message: err && err.message, data: null });
        })
      );
  }

  /**
   * Repaired/purchased parts detail for one piece of equipment (belonging to the current user).
   * GET /api/myEquipment/{id}/repairCostDetail
   */
  getEquipmentRepairCostDetail(
    equipmentId: string,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    const query: any = {};
    if (fromDate) query.fromDate = fromDate;
    if (toDate) query.toDate = toDate;
    return this.httpClient
      .get<any>(`${this.basePath}/api/myEquipment/${equipmentId}/repairCostDetail`, {
        params: query
      })
      .pipe(
        catchError((err) => {
          console.error('Error fetching equipment repair cost detail', err);
          return of({ success: false, message: err && err.message, data: [] });
        })
      );
  }

  private saveToStorage(): void {
    localStorage.setItem('my_equipment_list', JSON.stringify(this.equipmentList));
  }
}
